/**
 * Tiny app-level event bus for cross-cutting concerns the http client needs
 * to surface to the router (e.g. SubscriptionExpired → /suspended). Avoids
 * coupling the http client to react-router.
 */

export interface AppEventMap {
  'subscription-expired': {
    tenantId?: string;
    expiredAt?: string;
    requestId?: string;
  };
  /**
   * Emitted when the http client cannot authenticate (no session, refresh
   * failed). The router subscribes and routes to /login?from=…
   */
  'auth-required': {
    reason:
      | 'no-session'
      | 'refresh-failed'
      | 'logout'
      | 'token-expired'
      | 'retry-still-unauthorized';
  };
}

type Listener<K extends keyof AppEventMap> = (payload: AppEventMap[K]) => void;

const listeners: { [K in keyof AppEventMap]: Set<Listener<K>> } = {
  'subscription-expired': new Set(),
  'auth-required': new Set(),
};

export const appEvents = {
  on<K extends keyof AppEventMap>(name: K, fn: Listener<K>): () => void {
    listeners[name].add(fn);
    return () => {
      listeners[name].delete(fn);
    };
  },
  emit<K extends keyof AppEventMap>(name: K, payload: AppEventMap[K]): void {
    listeners[name].forEach((fn) => fn(payload));
  },
};
